import { Button, Col, Empty, Modal, Row } from 'antd';
import { useSession } from 'next-auth/react';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { MessageList, MessageListEvent, MessageType } from 'react-chat-elements';
import 'react-chat-elements/dist/main.css';
import { getI18n } from 'react-i18next';
import { sendSeen } from '../../../clients/chat/connection';
import { WsMessage } from '../../../clients/chat/types';
import usePageVisibility from '../../../hooks/usePageVisibility';
import useToggle from '../../../hooks/useToggle';
import { Attachment } from '../../../model/types';
import { useCurrentChatSessionContext } from '../../../store/provider/CurrentChatContextProvider';
import SanitizedHtmlComponent from '../../SanitizedHtmlComponent';
import { formatDate } from '../../TimeDisplay';
import ChatInput, { MediaFileMeta } from './ChatInput';
import { acceptedAudioTypeMap, acceptedFileTypeMap, acceptedImageTypeMap, acceptedVideoTypeMap } from './types';

const styles = {
    root: { padding: '0 1rem' },
    hidden: { display: 'none' },
    sticky: { backgroundColor: 'white', position: 'sticky', top: 0, zIndex: 1, paddingTop: '0.5rem' } as CSSProperties,
    chatHistoryLoading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }
};

interface Props {
    isVisible: boolean;
    pluginId: string;
}

export const CURRENT_USER_TEMP_MESSAGE_ID = '0';

const ACCEPTED_AUDIO_TYPE_LIST = Object.keys(acceptedAudioTypeMap);
const ACCEPTED_FILE_TYPE_LIST = Object.keys(acceptedFileTypeMap);
const ACCEPTED_IMAGE_TYPE_LIST = Object.keys(acceptedImageTypeMap);
const ACCEPTED_VIDEO_TYPE_LIST = Object.keys(acceptedVideoTypeMap);
export const ACCEPTED_ALL_TYPE_LIST = [
    ...ACCEPTED_AUDIO_TYPE_LIST,
    ...ACCEPTED_FILE_TYPE_LIST,
    ...ACCEPTED_IMAGE_TYPE_LIST,
    ...ACCEPTED_VIDEO_TYPE_LIST
];

function isValidUrl(urlString = '') {
    // eslint-disable-next-line
    const regExp = /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/gm;
    return urlString.match(regExp)?.length > 0;
}

function messageTypeFactory(message: WsMessage) {
    let tempContentType: MessageType['type'] = 'text';
    const mediaMetaData = parseMediaMetaData(message.text);

    if (typeof mediaMetaData === 'string' || typeof mediaMetaData === 'number') {
        tempContentType = 'text';
    } else {
        const contentType = mediaMetaData?.mimeType;
        if (!contentType) tempContentType = 'text';
        if (ACCEPTED_AUDIO_TYPE_LIST.includes(contentType)) tempContentType = 'audio';
        if (ACCEPTED_FILE_TYPE_LIST.includes(contentType)) tempContentType = 'file';
        if (ACCEPTED_IMAGE_TYPE_LIST.includes(contentType)) tempContentType = 'photo';
        if (ACCEPTED_VIDEO_TYPE_LIST.includes(contentType)) tempContentType = 'video';
        if (!ACCEPTED_ALL_TYPE_LIST.includes(contentType) && contentType !== 'text/html') tempContentType = 'file';
    }

    return tempContentType;
}

function parseMediaMetaData(messageText: string): MediaFileMeta | string {
    try {
        return JSON.parse(messageText);
    } catch (error) {
        return messageText;
    }
}
function parseSize(fileSizeInBytes: string | number) {
    const limit = 1024;
    const fileSizeInKBs = Math.round(Number(fileSizeInBytes) / limit);
    if (isNaN(fileSizeInKBs)) return '';

    const showInKBs = fileSizeInKBs < limit;
    return showInKBs ? `${fileSizeInKBs} KB` : `${Math.round(fileSizeInKBs / limit)} MB`;
}
function messageFactory(message: WsMessage) {
    const type = messageTypeFactory(message);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const additionalInfo: MessageType = { type };

    // https://github.com/Detaysoft/react-chat-elements/issues/76#issuecomment-432574500
    if (type === 'text') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        additionalInfo.text = <SanitizedHtmlComponent dirtyHtml={message.text} />;
    }

    const mediaMetaData = parseMediaMetaData(message.text);
    const url = typeof mediaMetaData === 'string' ? mediaMetaData : mediaMetaData.url;
    const fileName = (typeof mediaMetaData === 'string' ? mediaMetaData : mediaMetaData.fileName) ?? '';
    const size = typeof mediaMetaData === 'string' ? mediaMetaData : mediaMetaData.size;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (type === 'audio') additionalInfo.data = { audioURL: url };
    if (type === 'file') {
        additionalInfo.text = fileName;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        additionalInfo.data = {
            uri: url,
            size: parseSize(size)
        };
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (type === 'photo') additionalInfo.data = { uri: url };
    if (type === 'video')
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        additionalInfo.data = {
            videoURL: url,
            status: {
                click: true,
                loading: 0.5,
                download: true
            }
        };

    return additionalInfo;
}

function getDateString(date: number): string {
    const fromNow = formatDate(date, { fromNow: true, language: getI18n().language });
    const datePart = formatDate(date, { formatType: 'HH:mm, DD/MM/YYYY', language: getI18n().language });
    return `${datePart} (${fromNow})`;
}

function messageToBox(message: WsMessage, isMine: boolean): MessageType {
    // console.log(`messageToBox , message`, message);
    /*
    ┌─────────┬───────────────────┬───────────────┬───────────────────────┐
    │ 🕑      │ ✓                 │ ✓✓            │ ✓✓blue                │
    ├─────────┼───────────────────┼───────────────┼───────────────────────┤
    │ waiting │ sent              │ received      │ read                  │
    ├─────────┼───────────────────┼───────────────┼───────────────────────┤
    │ local   │ got receipt frame │ got ack frame │ got seen info message │
    │  render │                   │               │                       │
    └─────────┴───────────────────┴───────────────┴───────────────────────┘
   */
    let status = message.status ?? 'waiting';
    if (message.seenAt) {
        status = 'read';
    } else if (message.receivedAt) {
        status = 'received';
    } else if (message.fromHistory) {
        status = 'sent';
    }

    return {
        position: isMine ? 'right' : 'left',
        date: message.sentAt,
        dateString: getDateString(message.sentAt),
        title: isMine ? undefined : message.sender,
        status: isMine ? status : undefined,
        notch: true,
        ...messageFactory(message)
        //dateString: 'n/a',
        // avatar: isMine
        //   ? 'https://cdn2.iconfinder.com/data/icons/ios-7-icons/50/user_male3-512.png'
        //   : 'https://cdn2.iconfinder.com/data/icons/ios-7-icons/50/headset-512.png',
    } as MessageType;
}

// We need to prevent unmounting of this component to be sure that we fetch the chat history once on React creating this component for the first time. ref to @isVisible prop below
//@isVisible prop controls to show/hide chat app UI (e.g. chat input, message list, etc.) and this prop is controlled by @isChildrenVisible prop of AgentChatContainer component.
//@pluginId prop controls to show/hide chat input UI and this prop is controlled by AgentChatContainer component.

export default function AgentChatComponent({ isVisible, pluginId }: Props) {
    const messagesEndRef = useRef(null);
    const messageListReferance = useRef();

    const {
        data: {
            userInfo: { userId }
        }
    } = useSession();
    const [isPreviewOpen, , open, close] = useToggle();
    const [previewFile, setPreviewFile] = useState<Attachment>(null);
    const isTabVisible = usePageVisibility();
    const lastMessageIdRef = useRef<string>();

    const { chatSessionId, messages, subscribedToChatSession } = useCurrentChatSessionContext();
    useEffect(() => {
        if (chatSessionId && messages && subscribedToChatSession && isTabVisible) {
            const lastSeenMessageId = messages
                .filter(
                    (message) =>
                        message.senderId && message.senderId !== userId && !message.seenAt && message.senderId > 0
                )
                .map((message) => message.id)
                .at(-1);
            if (lastSeenMessageId && lastMessageIdRef.current !== lastSeenMessageId) {
                sendSeen(lastSeenMessageId, chatSessionId);
                lastMessageIdRef.current = lastSeenMessageId;
            }
        }
    }, [chatSessionId, userId, messages, subscribedToChatSession, isTabVisible]);

    useEffect(() => {
        messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    useEffect(() => {
        messagesEndRef?.current?.scrollIntoView();
    });
    const handleOnClick: MessageListEvent = (message, _i, e) => {
        if (message.type === 'text' && e?.target instanceof HTMLAnchorElement) return;
        e.preventDefault();

        if (message.type === 'photo' && e?.target instanceof HTMLImageElement) {
            setPreviewFile({ objectThumbUrl: message?.data?.uri, filename: 'preview' } as Attachment);
            open();
            return;
        }

        if (
            message.type === 'file' &&
            (e?.target instanceof HTMLDivElement ||
                e?.target instanceof SVGElement ||
                e?.target instanceof HTMLButtonElement)
        ) {
            let buttonNode: ParentNode | null = e.target;
            while (!(buttonNode instanceof HTMLButtonElement)) {
                buttonNode = buttonNode.parentNode;

                if (!buttonNode) return;
            }
            buttonNode.parentElement instanceof HTMLDivElement &&
                buttonNode.parentElement.className === 'rce-mbox-file' &&
                window.open(message?.data?.uri, '_blank');
            return;
        }
    };

    return (
        <Row justify="center" style={{ ...(isVisible ? styles.root : styles.hidden) }} gutter={10}>
            <Col xs={24} sm={24} md={24} lg={24} xl={18} xxl={13} className="agent-chat-message-list">
                {messages.length < 1 ? (
                    <Empty />
                ) : (
                    <MessageList
                        onClick={handleOnClick}
                        referance={messageListReferance}
                        lockable={true}
                        toBottomHeight={'100%'}
                        dataSource={messages.map((message) =>
                            messageToBox(
                                message,
                                message.id === CURRENT_USER_TEMP_MESSAGE_ID || message.senderId === userId
                            )
                        )}
                    />
                )}
            </Col>
            {pluginId ? (
                <Col xs={24} sm={24} md={24} lg={24} xl={18} xxl={13} className="chat-input" ref={messagesEndRef}>
                    <ChatInput isDisabled={!subscribedToChatSession} chatSessionId={chatSessionId} />
                </Col>
            ) : null}
            {previewFile && (
                <Modal
                    centered
                    className="chat-photo-preview "
                    open={isPreviewOpen}
                    title={
                        <Button type="primary" onClick={() => window.open(previewFile?.objectThumbUrl, '_blank')}>
                            Download
                        </Button>
                    }
                    footer={null}
                    onCancel={() => {
                        close();
                        setPreviewFile(null);
                    }}
                    destroyOnClose
                    width={'auto'}
                    style={{ maxWidth: '70%', minWidth: '30%' }}
                >
                    <img alt={previewFile?.filename} src={previewFile?.objectThumbUrl} style={{ maxWidth: '100%' }} />
                </Modal>
            )}
        </Row>
    );
}

export { isValidUrl, messageTypeFactory, parseMediaMetaData };
